import { useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from '@/components/ui/item';
import { cn } from '@/lib/utils';
import { registerFocIncidentsTourApi } from '../../focIncidentsTour/focIncidentsTourBridge';
import { useUscgMapContext } from '../LayersTab/UscgMapContext';
import { IncidentCardBody } from './IncidentCardBody';
import { IncidentMapButton } from './IncidentMapButton';
import type { IncidentListItemData } from './IncidentListItem';
import { getIncidentIcon } from './utils/incidentIconUtils';

export type IncidentListCardProps = {
  incident: IncidentListItemData;
  defaultCollapsed?: boolean;
  /** Map-visibility checkbox. Used on the Incidents tab only. */
  showMapVisibilityToggle?: boolean;
  /** First list row registers expand/collapse for the FOC 7.1 tour. */
  tourHost?: boolean;
};

export function IncidentListCard({
  incident,
  defaultCollapsed = true,
  showMapVisibilityToggle = false,
  tourHost = false,
}: IncidentListCardProps) {
  const [open, setOpen] = useState(!defaultCollapsed);
  const { isMapVisible, hiddenIncidentIds, toggleIncidentHiddenOnMap } =
    useUscgMapContext();

  useEffect(() => {
    if (!tourHost) return;
    return registerFocIncidentsTourApi({
      expandFirstIncident: () => setOpen(true),
      collapseFirstIncident: () => setOpen(false),
    });
  }, [tourHost]);

  const showCheckbox =
    showMapVisibilityToggle &&
    isMapVisible &&
    !!incident.latLong &&
    getIncidentIcon(incident.incidentTypes) !== null;

  const typeLabel =
    incident.incidentTypes.filter(Boolean).join(' · ') || 'Incident';

  return (
    <div className="dark tw:min-w-0 tw:max-w-full tw:text-foreground">
      <Item
        variant="outline"
        size="xs"
        className="tw:min-w-0 tw:max-w-full tw:flex-col tw:items-stretch tw:overflow-hidden tw:p-0 tw:bg-card"
      >
        <Collapsible
          open={open}
          onOpenChange={setOpen}
          className="tw:min-w-0 tw:w-full tw:overflow-hidden"
        >
          <div
            className="tw:flex tw:cursor-pointer tw:items-start tw:gap-2 tw:p-2.5 tw:transition-colors tw:hover:bg-muted/40"
            data-tour={tourHost ? 'incident-summary' : undefined}
            onClick={() => setOpen((prev) => !prev)}
          >
            {showCheckbox ? (
              <Checkbox
                checked={!hiddenIncidentIds.includes(incident.id)}
                className="tw:mt-1"
                aria-label={
                  !hiddenIncidentIds.includes(incident.id)
                    ? `Hide ${incident.name} on map`
                    : `Show ${incident.name} on map`
                }
                onClick={(event) => event.stopPropagation()}
                onCheckedChange={() => {
                  toggleIncidentHiddenOnMap(incident.id);
                }}
              />
            ) : null}
            <ItemContent className="tw:min-w-0 tw:flex-1 tw:gap-1">
              {incident.incidentTypes.length > 0 ? (
                <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-1.5">
                  {incident.incidentTypes.map((type) => (
                    <Badge key={type} variant="outline" className="tw:shrink-0">
                      {type}
                    </Badge>
                  ))}
                </div>
              ) : null}
              <ItemTitle className="tw:text-sm tw:font-medium tw:leading-snug">
                {incident.name}
              </ItemTitle>
              <ItemDescription className="tw:text-xs tw:text-muted-foreground">
                {[incident.aor, incident.createdAt]
                  .filter(Boolean)
                  .join(' · ') || typeLabel}
              </ItemDescription>
            </ItemContent>
            <ItemActions className="tw:shrink-0 tw:gap-1">
              {incident.latLong ? (
                <IncidentMapButton incident={incident} />
              ) : null}
              <CollapsibleTrigger
                render={
                  <Button
                    type="button"
                    size="icon-sm"
                    variant="ghost"
                    aria-label={open ? 'Collapse incident' : 'Expand incident'}
                    data-tour={tourHost ? 'incident-expand' : undefined}
                    onClick={(click) => click.stopPropagation()}
                  />
                }
              >
                <ChevronDown
                  className={cn(
                    'tw:h-4 tw:w-4 tw:transition-transform',
                    open && 'tw:rotate-180'
                  )}
                />
              </CollapsibleTrigger>
            </ItemActions>
          </div>
          <CollapsibleContent className="tw:min-w-0 tw:overflow-hidden">
            <div
              className="tw:min-w-0 tw:space-y-3 tw:overflow-hidden tw:border-t tw:border-border tw:px-2.5 tw:py-2"
              data-tour={tourHost ? 'incident-detail' : undefined}
            >
              <IncidentCardBody incident={incident} />
            </div>
          </CollapsibleContent>
        </Collapsible>
      </Item>
    </div>
  );
}
